import React from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { swedishVocabulary, VocabWord } from '../data/swedish';
import { checkAnswerMatch } from '../utils/textMatcher';

export const GET_PROGRESS = gql`
  query GetLanguageProgress($language: String!) {
    getLanguageProgress(language: $language) {
      id
      language
      setId
      wordId
      mode
      mastery
    }
  }
`;

export const UPDATE_PROGRESS = gql`
  mutation UpdateWordProgress($language: String!, $setId: Int!, $wordId: Int!, $mastery: Int!, $mode: String) {
    updateWordProgress(language: $language, setId: $setId, wordId: $wordId, mastery: $mastery, mode: $mode) {
      id
      language
      setId
      wordId
      mode
      mastery
    }
  }
`;

export const RESET_PROGRESS = gql`
  mutation ResetSetProgress($language: String!, $setId: Int!, $mode: String) {
    resetSetProgress(language: $language, setId: $setId, mode: $mode)
  }
`;

export interface UseQuizGameProps {
  setId: number;
}

export const useQuizGame = ({ setId }: UseQuizGameProps) => {
  const { loading, error, data } = useQuery(GET_PROGRESS, {
    variables: { language: 'swedish' },
  });

  const [updateWordProgress] = useMutation(UPDATE_PROGRESS);
  const [resetSetProgress] = useMutation(RESET_PROGRESS);

  const [localProgress, setLocalProgress] = React.useState<{ [key: string]: number }>({});
  const localProgressRef = React.useRef(localProgress);
  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    localProgressRef.current = localProgress;
  }, [localProgress]);

  React.useEffect(() => {
    if (data?.getLanguageProgress) {
      const initialMap: { [key: string]: number } = {};
      data.getLanguageProgress.forEach((p: any) => {
        const pMode = p.mode || 'normal';
        initialMap[`${pMode}_${p.setId}_${p.wordId}`] = p.mastery;
      });
      setLocalProgress(initialMap);
      localProgressRef.current = initialMap;
      setIsInitialized(true);
    }
  }, [data]);

  // Game states
  const [mode, setMode] = React.useState<'normal' | 'reverse' | 'fill'>('normal');
  const isReverse = mode === 'reverse';
  const [currentWord, setCurrentWord] = React.useState<VocabWord | null>(null);
  const [userInput, setUserInput] = React.useState<string>('');
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);
  const [feedback, setFeedback] = React.useState<{ status: 'correct' | 'incorrect' | null; message: string }>({
    status: null,
    message: '',
  });
  const [isResetConfirmOpen, setIsResetConfirmOpen] = React.useState(false);

  const lastWordRef = React.useRef<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const getFillSentence = React.useCallback((word: VocabWord) => {
    const sentence = word.sentence || `Här är ${word.swedish}.`;
    const escaped = word.swedish.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(`\\b${escaped}\\b`, 'gi');
    let replaced = sentence.replace(regex, '___');
    if (replaced === sentence) {
      const idx = sentence.toLowerCase().indexOf(word.swedish.toLowerCase());
      if (idx >= 0) {
        replaced = sentence.substring(0, idx) + '___' + sentence.substring(idx + word.swedish.length);
      }
    }
    return replaced;
  }, []);

  const setWords = React.useMemo(() => {
    return swedishVocabulary.filter((word) => word.setId === setId);
  }, [setId]);

  const stats = React.useMemo(() => {
    const total = setWords.length;
    const mastered = setWords.filter((word) => (localProgress[`${mode}_${word.setId}_${word.wordId}`] || 0) >= 5).length;
    const remaining = total - mastered;
    const name = setWords[0]?.setName || 'Set';
    return { name, total, mastered, remaining };
  }, [setWords, localProgress, mode]);

  const selectNextWord = React.useCallback(() => {
    const unmastered = setWords.filter((word) => (localProgressRef.current[`${mode}_${word.setId}_${word.wordId}`] || 0) < 5);
    
    if (unmastered.length === 0) {
      setCurrentWord(null);
      return;
    }

    let pool = unmastered;
    if (pool.length > 1 && lastWordRef.current) {
      const filtered = pool.filter((w) => (isReverse ? w.english : w.swedish) !== lastWordRef.current);
      if (filtered.length > 0) {
        pool = filtered;
      }
    }

    const randomIndex = Math.floor(Math.random() * pool.length);
    const nextWord = pool[randomIndex];
    setCurrentWord(nextWord);
    lastWordRef.current = isReverse ? nextWord.english : nextWord.swedish;
    setUserInput('');
    setFeedback({ status: null, message: '' });
    
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  }, [setWords, mode, isReverse]);

  React.useEffect(() => {
    if (isInitialized && !loading && !error) {
      selectNextWord();
    }
  }, [setId, isInitialized, loading, error, mode, selectNextWord]);

  const handleUpdateProgress = (wordId: number, mastery: number) => {
    const key = `${mode}_${setId}_${wordId}`;
    setLocalProgress((prev) => {
      const next = { ...prev, [key]: mastery };
      localProgressRef.current = next;
      return next;
    });

    updateWordProgress({
      variables: { language: 'swedish', setId, wordId, mastery, mode },
      refetchQueries: ['GetLanguageProgress'],
    }).catch((err) => {
      console.error('Failed to update progress in background:', err);
    });
  };

  const triggerResetSet = () => {
    setIsResetConfirmOpen(true);
  };

  const handleConfirmReset = () => {
    setIsResetConfirmOpen(false);
    const updatedLocalProgress = { ...localProgress };
    setWords.forEach((word) => {
      updatedLocalProgress[`${mode}_${setId}_${word.wordId}`] = 0;
    });
    setLocalProgress(updatedLocalProgress);
    localProgressRef.current = updatedLocalProgress;

    resetSetProgress({
      variables: { language: 'swedish', setId, mode },
      refetchQueries: ['GetLanguageProgress'],
    }).catch((err) => {
      console.error('Failed to reset set progress in background:', err);
    });

    lastWordRef.current = null;
    selectNextWord();
  };

  const handleCancelReset = () => {
    setIsResetConfirmOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isProcessing || !currentWord) return;

    const trimmedInput = userInput.trim();
    if (!trimmedInput) return;

    setIsProcessing(true);
    const currentMastery = localProgress[`${mode}_${currentWord.setId}_${currentWord.wordId}`] || 0;
    const answerToMatch = mode === 'normal' ? currentWord.english : currentWord.swedish;
    const isCorrect = checkAnswerMatch(trimmedInput, answerToMatch);

    let delay = 1000;
    if (isCorrect) {
      const newMastery = Math.min(currentMastery + 1, 5);
      const newlyMastered = newMastery === 5;
      
      setFeedback({
        status: 'correct',
        message: newlyMastered
          ? `🎉 Correct! Word Mastered! (${newMastery}/5)`
          : `✅ Correct! (${newMastery}/5 in a row)`,
      });

      handleUpdateProgress(currentWord.wordId, newMastery);
      if (newlyMastered) delay = 1200;
    } else {
      setFeedback({
        status: 'incorrect',
        message: `❌ Incorrect. The correct translation is "${answerToMatch}". Progress reset!`,
      });
      handleUpdateProgress(currentWord.wordId, 0);
      delay = 2000;
    }

    setTimeout(() => {
      selectNextWord();
      setIsProcessing(false);
    }, delay);
  };

  const currentWordMastery = currentWord ? localProgress[`${mode}_${currentWord.setId}_${currentWord.wordId}`] || 0 : 0;
  const isHighlighted = !!feedback.status;

  return {
    loading,
    error,
    mode,
    setMode,
    isReverse,
    currentWord,
    userInput,
    setUserInput,
    isProcessing,
    feedback,
    stats,
    currentWordMastery,
    isHighlighted,
    isResetConfirmOpen,
    inputRef,
    getFillSentence,
    triggerResetSet,
    handleConfirmReset,
    handleCancelReset,
    handleSubmit,
  };
};
